import {
    UserInfo,
    Job,
    Application,
    FileTransfer,
    Dataset,
    Image,
    BillRecord,
    RechargeRecord,
    SSHNode,
    SystemStats,
    Ticket,
    Voucher,
    User,
    SubUser,
    ClusterConfig,
    BillingSummary,
    JobCostDetail,
    ResourcePricing,
    ComputePrice,
    StorageSystem,
    AIProject,
    AIProjectTemplate
} from '../types/api';

// 扩展数据类型
export interface Workstation {
    id: string;
    name: string;
    type: 'windows' | 'linux';
    status: 'running' | 'stopped' | 'starting' | 'stopping';
    cpuCores: number;
    memory: number;
    storage: number;
    cost: number;
    createdAt: string;
    lastAccessAt?: string;
    template?: string;
}

export interface Software {
    id: string;
    name: string;
    version: string;
    category: string;
    description: string;
    icon: string;
    status: 'available' | 'installing' | 'pending_approval';
    license: 'free' | 'commercial';
    systemRequirements: string[];
    installationGuide: string;
    tags: string[];
}

export interface UserProfile {
    id: string;
    username: string;
    email: string;
    phone?: string;
    company?: string;
    department?: string;
    position?: string;
    balance: number;
    userType: 'standard' | 'enterprise' | 'dedicated' | 'private';
    accountStatus: 'active' | 'suspended' | 'pending';
    createdAt: string;
    lastLoginAt?: string;
    avatar?: string;
}

export interface KeyPair {
    id: string;
    name: string;
    fingerprint: string;
    publicKey: string;
    createdAt: string;
    lastUsedAt?: string;
    status: 'active' | 'inactive';
}

export interface NetworkPolicy {
    id: string;
    name: string;
    type: 'ingress' | 'egress';
    protocol: 'tcp' | 'udp';
    port: string;
    source: string;
    action: 'allow' | 'deny';
    status: 'active' | 'inactive';
    createdAt: string;
}

export interface AuditLog {
    id: string;
    userId: string;
    action: string;
    resource: string;
    result: 'success' | 'failed';
    ipAddress: string;
    userAgent: string;
    createdAt: string;
    details?: string;
}

export interface TeamMember {
    id: string;
    username: string;
    email: string;
    role: 'admin' | 'member' | 'viewer';
    status: 'active' | 'inactive';
    joinedAt: string;
    lastActiveAt?: string;
    permissions: string[];
}

export interface Quota {
    id: string;
    type: 'cpu' | 'memory' | 'storage' | 'gpu';
    name: string;
    allocated: number;
    used: number;
    unit: string;
    status: 'normal' | 'warning' | 'exceeded';
    lastUpdated: string;
}

// 用户信息
export const mockUser: UserInfo = {
    id: 'user_001',
    username: 'ssjmhyvi',
    email: 'user@chaincloud.com',
    balance: 1250.50,
    userType: 'standard',
    createdAt: '2024-01-15T08:30:00Z',
    lastLoginAt: '2024-12-20T14:22:00Z'
};

// 作业列表
export const mockJobs: Job[] = [
    {
        id: 'job_001',
        name: 'GROMACS分子动力学模拟',
        status: 'running',
        user: 'user1',
        submitTime: '2024-01-15 10:00:00',
        startTime: '2024-01-15 10:01:00',
        nodes: 2,
        cores: 16,
        app: 'GROMACS',
        application: 'GROMACS',
        progress: 45,
        masterId: 'master1',
        requestId: 'req1',
        createdAt: '2024-01-15 10:00:00'
    },
    {
        id: 'job_002',
        name: 'VASP材料计算',
        status: 'completed',
        user: 'user2',
        submitTime: '2024-01-14 15:00:00',
        startTime: '2024-01-14 15:01:00',
        endTime: '2024-01-14 18:00:00',
        nodes: 1,
        cores: 8,
        app: 'VASP',
        application: 'VASP',
        progress: 100,
        masterId: 'master1',
        requestId: 'req2',
        createdAt: '2024-01-14 15:00:00'
    },
    {
        id: 'job_003',
        name: 'TensorFlow训练',
        status: 'pending',
        user: 'user1',
        submitTime: '2024-01-15 11:00:00',
        nodes: 1,
        cores: 4,
        app: 'TensorFlow',
        application: 'TensorFlow',
        masterId: 'master1',
        requestId: 'req3',
        createdAt: '2024-01-15 11:00:00'
    }
];

// 应用中心
export const mockApplications: Application[] = [
    {
        id: 'app_001',
        name: 'GROMACS',
        version: '2021.4',
        category: 'molecular_dynamics',
        description: '分子动力学模拟软件',
        icon: '🧪',
        downloads: 8760,
        rating: 4.6,
        size: '450 MB',
        isInstalled: true,
        tags: ['分子模拟', 'C++', 'MPI']
    },
    {
        id: 'app_002',
        name: 'TensorFlow',
        version: '2.8.0',
        category: 'machine_learning',
        description: '机器学习框架',
        icon: '🧠',
        downloads: 15420,
        rating: 4.8,
        size: '2.3 GB',
        isInstalled: true,
        tags: ['深度学习', 'Python', 'GPU加速']
    },
    {
        id: 'app_003',
        name: 'PyTorch',
        version: '2.1.2',
        category: '机器学习',
        description: '动态神经网络框架，研究友好的深度学习库',
        icon: '🔥',
        downloads: 12350,
        rating: 4.7,
        size: '1.8 GB',
        isInstalled: false,
        tags: [ '深度学习', 'Python', '动态图' ]
    },
    {
        id: 'app_004',
        name: 'ANSYS Fluent',
        version: '2024 R1',
        category: '计算流体力学',
        description: '专业的CFD仿真软件',
        icon: '🌊',
        downloads: 3420,
        rating: 4.5,
        size: '3.2 GB',
        isInstalled: false,
        tags: [ 'CFD', '仿真', '工程' ]
    }
];

// 文件传输
export const mockFileTransfers: FileTransfer[] = [
    {
        id: 'transfer_001',
        fileName: 'dataset.zip',
        fileSize: 1024000,
        type: 'upload',
        status: 'completed',
        progress: 100,
        speed: 10,
        createdAt: '2024-01-15T10:00:00Z',
        completedAt: '2024-01-15T10:05:00Z'
    },
    {
        id: 'transfer_002',
        fileName: 'simulation_results.tar.gz',
        fileSize: 524288000, // 500MB
        type: 'download',
        status: 'transferring',
        progress: 65,
        speed: 15728640, // 15MB/s
        createdAt: '2024-12-20T14:20:00Z'
    },
    {
        id: 'transfer_003',
        fileName: 'model_checkpoint.h5',
        fileSize: 104857600, // 100MB
        type: 'upload',
        status: 'failed',
        progress: 23,
        speed: 0,
        createdAt: '2024-12-20T13:15:00Z'
    }
];

// 数据集
export const mockDatasets: Dataset[] = [
    {
        id: 'dataset_001',
        name: 'ImageNet-1K',
        description: '大规模图像识别数据集，包含1000个类别的120万张图片',
        size: 144115188075, // 134GB
        type: 'public',
        category: '计算机视觉',
        downloadCount: 2340,
        createdAt: '2024-01-15T00:00:00Z',
        updatedAt: '2024-12-01T12:00:00Z',
        tags: [ '图像分类', '深度学习', '计算机视觉' ]
    },
    {
        id: 'dataset_002',
        name: 'Protein Structure DB',
        description: '私有蛋白质结构数据库，用于分子动力学研究',
        size: 21474836480, // 20GB
        type: 'private',
        category: '生物信息学',
        downloadCount: 156,
        createdAt: '2024-03-20T10:30:00Z',
        updatedAt: '2024-12-15T16:45:00Z',
        tags: [ '蛋白质', '结构生物学', '分子模拟' ]
    },
    {
        id: 'dataset_003',
        name: 'Weather Data 2024',
        description: '全球气象数据集，包含温度、湿度、风速等参数',
        size: 5368709120, // 5GB
        type: 'public',
        category: '气象学',
        downloadCount: 890,
        createdAt: '2024-06-01T00:00:00Z',
        updatedAt: '2024-12-20T00:00:00Z',
        tags: [ '气象', '时间序列', '预测' ]
    }
];

// 镜像中心
export const mockImages: Image[] = [
    {
        id: 'image_001',
        name: 'ubuntu-ml',
        tag: '22.04-cuda12.2',
        type: 'container',
        size: 3221225472, // 3GB
        description: 'Ubuntu 22.04 with CUDA 12.2 and ML frameworks',
        status: 'available',
        createdAt: '2024-11-15T10:00:00Z',
        downloads: 1250
    },
    {
        id: 'image_002',
        name: 'centos-hpc',
        tag: '8-openmpi',
        type: 'vm',
        size: 2147483648, // 2GB
        description: 'CentOS 8 with OpenMPI for HPC applications',
        status: 'available',
        createdAt: '2024-10-20T14:30:00Z',
        downloads: 680
    },
    {
        id: 'image_003',
        name: 'pytorch-dev',
        tag: '2.1-gpu',
        type: 'container',
        size: 4294967296, // 4GB
        description: 'PyTorch development environment with GPU support',
        status: 'building',
        createdAt: '2024-12-20T16:00:00Z',
        downloads: 0
    }
];

// 账单记录
export const mockBillRecords: BillRecord[] = [
    {
        id: 'bill_001',
        date: '2024-01-15 14:30:00',
        type: 'compute',
        description: 'GPU计算节点 - GROMACS分子动力学',
        amount: -45.60,
        duration: '6小时',
        resource: 'g-v100-1',
        jobId: '22',
        period: '2024-01',
        productType: 'GPU'
    },
    {
        id: 'bill_002',
        date: '2024-01-15 09:15:00',
        type: 'storage',
        description: '文件存储 - CPFS-G高性能存储',
        amount: -12.30,
        duration: '24小时',
        resource: '200GB',
        period: '2024-01',
        productType: '存储'
    },
    {
        id: 'bill_003',
        date: '2024-01-14 16:45:00',
        type: 'compute',
        description: 'CPU计算节点 - TensorFlow训练',
        amount: -28.90,
        duration: '8小时',
        resource: 'c-16-4',
        jobId: '23',
        period: '2024-01',
        productType: 'CPU'
    },
    {
        id: 'bill_004',
        date: '2024-01-14 10:00:00',
        type: 'recharge',
        description: '账户充值',
        amount: 1000.00,
        period: '2024-01',
        productType: '充值'
    }
];

// 充值记录
export const mockRechargeRecords: RechargeRecord[] = [
    {
        id: 'recharge_001',
        amount: 1000,
        paymentMethod: 'alipay',
        status: 'completed',
        createdAt: '2024-01-15T10:00:00Z',
        completedAt: '2024-01-15T10:01:00Z',
        transactionId: 'alipay_20240115100000'
    },
    {
        id: 'recharge_002',
        amount: 1000,
        paymentMethod: 'alipay',
        status: 'completed',
        createdAt: '2024-11-20T14:20:00Z',
        completedAt: '2024-11-20T14:21:00Z',
        transactionId: 'ap_20241120142000'
    },
    {
        id: 'recharge_003',
        amount: 200,
        paymentMethod: 'wechat',
        status: 'pending',
        createdAt: '2024-12-20T16:45:00Z',
        transactionId: 'wx_20241220164500'
    }
];

// SSH节点
export const mockSSHNodes: SSHNode[] = [
    {
        id: 'node_001',
        name: 'compute-001',
        host: 'compute001.cluster.local',
        port: 22,
        status: 'running',
        cpuUsage: 45,
        memoryUsage: 60,
        createdAt: '2024-01-15T00:00:00Z',
        lastAccessAt: '2024-01-15T10:00:00Z'
    },
    {
        id: 'ssh_002',
        name: 'gpu-node-01',
        host: 'gpu01.chaincloud.com',
        port: 22,
        status: 'stopped',
        cpuUsage: 0,
        memoryUsage: 0,
        createdAt: '2024-12-19T15:20:00Z',
        lastAccessAt: '2024-12-19T18:45:00Z'
    }
];

// 系统统计
export const mockSystemStats: SystemStats = {
    totalJobs: 1247,
    runningJobs: 23,
    completedJobs: 1198,
    failedJobs: 26,
    totalCpuHours: 15420.5,
    totalCost: 8750.30,
    activeUsers: 156,
    systemLoad: 0.68
};

// 工作站数据
export const mockWorkstations: Workstation[] = [
    {
        id: 'ws_001',
        name: 'Windows工作站-01',
        type: 'windows',
        status: 'running',
        cpuCores: 8,
        memory: 32,
        storage: 500,
        cost: 2.5,
        createdAt: '2024-12-20T09:00:00Z',
        lastAccessAt: '2024-12-20T14:30:00Z',
        template: 'Windows Server 2019 + Visual Studio'
    },
    {
        id: 'ws_002',
        name: 'Linux工作站-01',
        type: 'linux',
        status: 'stopped',
        cpuCores: 16,
        memory: 64,
        storage: 1000,
        cost: 3.8,
        createdAt: '2024-12-19T15:20:00Z',
        lastAccessAt: '2024-12-19T18:45:00Z',
        template: 'CentOS 7 + Development Tools'
    },
    {
        id: 'ws_003',
        name: 'GPU工作站-01',
        type: 'linux',
        status: 'starting',
        cpuCores: 32,
        memory: 128,
        storage: 2000,
        cost: 8.5,
        createdAt: '2024-12-20T16:00:00Z',
        template: 'Ubuntu 20.04 + CUDA 11.8'
    }
];

// 软件库数据
export const mockSoftwareLibrary: Software[] = [
    {
        id: 'soft_001',
        name: 'MATLAB',
        version: 'R2024a',
        category: '数值计算',
        description: '技术计算平台，用于数据分析、算法开发和模型创建',
        icon: '📊',
        status: 'available',
        license: 'commercial',
        systemRequirements: [ 'Windows 10+', 'Linux CentOS 7+', '8GB RAM', '4GB存储' ],
        installationGuide: '1. 下载安装包\n2. 运行安装程序\n3. 输入许可证密钥\n4. 完成安装',
        tags: [ '数值计算', '数据分析', '仿真' ]
    },
    {
        id: 'soft_002',
        name: 'Gaussian',
        version: '16 Rev C.02',
        category: '量子化学',
        description: '量子化学计算软件包，用于分子结构优化和性质计算',
        icon: '⚛️',
        status: 'pending_approval',
        license: 'commercial',
        systemRequirements: [ 'Linux x86_64', '16GB RAM', '50GB存储' ],
        installationGuide: '需要商业许可证，请联系管理员申请',
        tags: [ '量子化学', '分子计算', 'DFT' ]
    },
    {
        id: 'soft_003',
        name: 'OpenFOAM',
        version: '11',
        category: '计算流体力学',
        description: '开源CFD工具包，用于流体力学数值模拟',
        icon: '🌊',
        status: 'available',
        license: 'free',
        systemRequirements: [ 'Linux', '8GB RAM', '10GB存储' ],
        installationGuide: '1. 下载源码\n2. 编译安装\n3. 配置环境变量',
        tags: [ 'CFD', '开源', '流体力学' ]
    },
    {
        id: 'soft_004',
        name: 'LAMMPS',
        version: '2023.08.02',
        category: '分子动力学',
        description: '大规模原子/分子并行模拟器',
        icon: '🧪',
        status: 'installing',
        license: 'free',
        systemRequirements: [ 'Linux', 'MPI', '4GB RAM' ],
        installationGuide: '正在安装中，预计完成时间: 30分钟',
        tags: [ '分子动力学', 'MPI', '并行计算' ]
    }
];

// 用户资料数据
export const mockUserProfile: UserProfile = {
    id: 'user_001',
    username: 'ssjmhyvi',
    email: 'user@chaincloud.com',
    phone: '+86 138****8888',
    company: '北京大学',
    department: '计算机科学与技术系',
    position: '研究生',
    balance: 1250.50,
    userType: 'standard',
    accountStatus: 'active',
    createdAt: '2024-01-15T08:30:00Z',
    lastLoginAt: '2024-12-20T14:22:00Z',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=ssjmhyvi'
};

// 工单数据
export const mockTickets: Ticket[] = [
    {
        id: 'ticket_001',
        username: 'user1',
        subject: '计算节点无法访问',
        status: 'pending',
        handler: 'admin1',
        createdAt: '2024-01-15T10:00:00Z',
        priority: 'high',
        category: 'infrastructure',
        description: '无法连接到计算节点，请协助检查'
    },
    {
        id: 'TK-2024001',
        username: 'user1',
        subject: 'GPU节点无法启动',
        status: 'pending',
        handler: '待分配',
        createdAt: '2024-01-15 14:30:00',
        description: 'V100节点提交作业后无法正常启动，报错信息如下...',
        priority: 'high',
        category: '技术支持',
        comments: [
            {
                id: 'CM-001',
                ticketId: 'TK-2024001',
                username: 'user1',
                content: '补充：尝试重新提交作业，仍然失败',
                createdAt: '2024-01-15 14:35:00'
            }
        ]
    },
    {
        id: 'TK-2024002',
        username: 'user2',
        subject: '账户充值问题',
        status: 'processing',
        handler: '客服小王',
        createdAt: '2024-01-15 15:45:00',
        description: '通过支付宝充值1000元未到账',
        priority: 'medium',
        category: '账户服务',
        comments: [
            {
                id: 'CM-002',
                ticketId: 'TK-2024002',
                username: '客服小王',
                content: '已查询到支付记录，正在处理中',
                createdAt: '2024-01-15 16:00:00'
            }
        ]
    }
];

// 代金券数据
export const mockVouchers: Voucher[] = [
    {
        id: 'V-2024001',
        code: 'WELCOME2024',
        amount: 100,
        type: 'fixed',
        status: 'unused',
        validFrom: '2024-01-01',
        validTo: '2024-12-31',
        description: '新用户欢迎券',
        restrictions: [ '仅限新用户使用', '最低消费500元' ],
        minSpend: 500,
        applicableProducts: [ 'all' ]
    },
    {
        id: 'V-2024002',
        code: 'GPU50OFF',
        amount: 50,
        type: 'percentage',
        status: 'unused',
        validFrom: '2024-01-01',
        validTo: '2024-06-30',
        description: 'GPU节点优惠券',
        restrictions: [ '仅限GPU节点使用', '单次最高优惠1000元' ],
        maxDiscount: 1000,
        applicableProducts: [ 'gpu' ]
    },
    {
        id: 'V-2024003',
        code: 'NEWYEAR500',
        amount: 500,
        type: 'fixed',
        status: 'used',
        validFrom: '2024-01-01',
        validTo: '2024-01-31',
        description: '新年特惠券',
        usedAt: '2024-01-10',
        usedBy: 'user1'
    }
];

// 密钥对数据
export const mockKeyPairs: KeyPair[] = [
    {
        id: 'key_001',
        name: 'my-laptop-key',
        fingerprint: 'SHA256:nThbg6kXUpJWGl7E1IGOCspRomTxdCARLviKw6E5SY8',
        publicKey: 'ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC7...',
        createdAt: '2024-11-15T10:00:00Z',
        lastUsedAt: '2024-12-20T14:30:00Z',
        status: 'active'
    },
    {
        id: 'key_002',
        name: 'server-backup-key',
        fingerprint: 'SHA256:bXGhg7kXUpJWGl7E1IGOCspRomTxdCARLviKw6E5SY9',
        publicKey: 'ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQD8...',
        createdAt: '2024-10-20T14:30:00Z',
        status: 'inactive'
    }
];

// 网络策略数据
export const mockNetworkPolicies: NetworkPolicy[] = [
    {
        id: 'policy_001',
        name: 'SSH访问策略',
        type: 'ingress',
        protocol: 'tcp',
        port: '22',
        source: '0.0.0.0/0',
        action: 'allow',
        status: 'active',
        createdAt: '2024-11-01T00:00:00Z'
    },
    {
        id: 'policy_002',
        name: 'HTTP服务策略',
        type: 'ingress',
        protocol: 'tcp',
        port: '80,443',
        source: '10.0.0.0/8',
        action: 'allow',
        status: 'active',
        createdAt: '2024-11-01T00:00:00Z'
    },
    {
        id: 'policy_003',
        name: '限制外网访问',
        type: 'egress',
        protocol: 'tcp',
        port: '80,443',
        source: '192.168.1.0/24',
        action: 'deny',
        status: 'inactive',
        createdAt: '2024-12-01T00:00:00Z'
    }
];

// 操作审计数据
export const mockAuditLogs: AuditLog[] = [
    {
        id: 'audit_001',
        userId: 'user_001',
        action: '登录系统',
        resource: 'auth',
        result: 'success',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        createdAt: '2024-12-20T14:22:00Z'
    },
    {
        id: 'audit_002',
        userId: 'user_001',
        action: '提交作业',
        resource: 'job_001',
        result: 'success',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        createdAt: '2024-12-20T10:30:00Z',
        details: '提交TensorFlow训练作业'
    },
    {
        id: 'audit_003',
        userId: 'user_001',
        action: '删除文件',
        resource: 'file_transfer',
        result: 'failed',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        createdAt: '2024-12-20T13:45:00Z',
        details: '权限不足，无法删除系统文件'
    }
];

// 团队成员数据
export const mockTeamMembers: TeamMember[] = [
    {
        id: 'member_001',
        username: 'ssjmhyvi',
        email: 'user@chaincloud.com',
        role: 'admin',
        status: 'active',
        joinedAt: '2024-01-15T08:30:00Z',
        lastActiveAt: '2024-12-20T14:22:00Z',
        permissions: [ '*' ]
    },
    {
        id: 'member_002',
        username: 'researcher01',
        email: 'researcher01@chaincloud.com',
        role: 'member',
        status: 'active',
        joinedAt: '2024-03-20T10:00:00Z',
        lastActiveAt: '2024-12-19T16:30:00Z',
        permissions: [ 'job.create', 'job.view', 'file.upload', 'file.download' ]
    },
    {
        id: 'member_003',
        username: 'intern01',
        email: 'intern01@chaincloud.com',
        role: 'viewer',
        status: 'inactive',
        joinedAt: '2024-06-01T09:00:00Z',
        lastActiveAt: '2024-11-30T17:00:00Z',
        permissions: [ 'job.view', 'file.view' ]
    }
];

// 配额数据
export const mockQuotas: Quota[] = [
    {
        id: 'quota_001',
        type: 'cpu',
        name: 'CPU配额',
        allocated: 1000,
        used: 245,
        unit: '核时',
        status: 'normal',
        lastUpdated: '2024-12-20T15:00:00Z'
    },
    {
        id: 'quota_002',
        type: 'memory',
        name: '内存配额',
        allocated: 2000,
        used: 1650,
        unit: 'GB时',
        status: 'warning',
        lastUpdated: '2024-12-20T15:00:00Z'
    },
    {
        id: 'quota_003',
        type: 'storage',
        name: '存储配额',
        allocated: 500,
        used: 520,
        unit: 'GB',
        status: 'exceeded',
        lastUpdated: '2024-12-20T15:00:00Z'
    },
    {
        id: 'quota_004',
        type: 'gpu',
        name: 'GPU配额',
        allocated: 100,
        used: 25,
        unit: '卡时',
        status: 'normal',
        lastUpdated: '2024-12-20T15:00:00Z'
    }
];

// 价格标准
export const mockComputePrices: ComputePrice[] = [
    {
        type: '海星',
        model: 'NVIDIA Tesla V100',
        cores: 10,
        gpus: '1',
        memory: '40GB/节点',
        queue: 'g-v100-1',
        standardPrice: '¥4/卡时',
        economyPrice: '—',
        region: 'GPU计算区'
    },
    {
        type: '海星',
        model: 'Intel Xeon Platinum',
        cores: 4,
        gpus: '—',
        memory: '16GB/节点',
        queue: 'c-4-4',
        standardPrice: '¥0.1/核时',
        economyPrice: '—',
        region: '通用计算区'
    }
];

// 存储系统配置
export const mockStorageSystems: StorageSystem[] = [
    {
        name: '高性能并行文件系统',
        specs: '100TB容量，10GB/s速度',
        price: '¥2.5/小时/TB'
    },
    {
        name: '对象存储系统',
        specs: '500TB容量，5GB/s速度', 
        price: '¥0.5/小时/TB'
    }
];

// 管理员用户
export const mockAdmins: User[] = [
    {
        id: 'admin-1',
        username: 'admin',
        email: 'admin@chaincloud.com',
        phone: '13800138000',
        role: 'org_admin',
        status: 'active',
        createdAt: '2024-01-01T00:00:00Z'
    }
];

// 子用户
export const mockSubUsers: SubUser[] = [
    {
        id: 'sub-1',
        username: 'developer1',
        email: 'dev1@example.com',
        role: 'user',
        monthlyQuota: 5000,
        yearlyQuota: 50000,
        usedQuota: 1200,
        createdAt: '2024-06-01T00:00:00Z'
    },
    {
        id: 'sub-2',
        username: 'analyst1',
        email: 'analyst1@example.com',
        role: 'user',
        monthlyQuota: 3000,
        yearlyQuota: 30000,
        usedQuota: 850,
        createdAt: '2024-06-10T00:00:00Z'
    }
];

// 账单摘要
export const mockBillingSummary: BillingSummary = {
    totalAmount: 2580.50,
    thisMonth: 485.30,
    lastMonth: 672.80,
    balance: 1250.00,
    credits: 200.00
};

// 集群配置
export const mockClusterConfig: ClusterConfig = {
    masterId: 'master-001',
    fileSystem: {
        enabled: true,
        fileSystemType: 'cpfs-g',
        capacity: 1000,
        bandwidth: 10,
        storageType: 'standard'
    },
    computeNode: {
        systemDiskSize: 500,
        systemDiskCategory: 'CLOUD_SSD'
    }
};

export const mockJobCostDetails: JobCostDetail[] = [
    {
        jobId: '22',
        jobName: '分子动力学模拟-蛋白质折叠',
        startTime: '2024-01-15 09:32:00',
        endTime: '2024-01-15 15:32:00',
        duration: '6小时',
        nodes: 1,
        cores: 8,
        nodeType: 'g-v100-1',
        computeCost: 43.20,
        storageCost: 2.40,
        networkCost: 0.00,
        totalCost: 45.60
    },
    {
        jobId: '23',
        jobName: 'CNN模型训练-图像识别',
        startTime: '2024-01-14 08:17:00',
        endTime: '2024-01-14 16:17:00',
        duration: '8小时',
        nodes: 2,
        cores: 32,
        nodeType: 'c-16-4',
        computeCost: 26.40,
        storageCost: 1.80,
        networkCost: 0.70,
        totalCost: 28.90
    }
];

export const mockResourcePricing: ResourcePricing[] = [
    { resource: 'c-4-1', type: 'CPU节点', price: '¥0.80/核·小时', description: '4核1G，通用计算' },
    { resource: 'c-8-2', type: 'CPU节点', price: '¥0.85/核·小时', description: '8核2G，内存优化' },
    { resource: 'c-16-4', type: 'CPU节点', price: '¥0.90/核·小时', description: '16核4G，高性能计算' },
    { resource: 'g-v100-1', type: 'GPU节点', price: '¥7.20/卡·小时', description: 'NVIDIA V100，深度学习' },
    { resource: 'g-a100-1', type: 'GPU节点', price: '¥12.80/卡·小时', description: 'NVIDIA A100，AI训练' },
    { resource: 'storage-standard', type: '标准存储', price: '¥0.12/GB·天', description: 'CPFS标准存储' },
    { resource: 'storage-advanced', type: '高级存储', price: '¥0.18/GB·天', description: 'CPFS高性能存储' }
];

// AI项目相关数据
export const mockAIProjects: AIProject[] = [
    {
        id: 'ai_project_001',
        name: '计算机视觉项目',
        description: '基于深度学习的图像分类与目标检测项目',
        type: 'classification',
        framework: 'TensorFlow',
        status: 'running',
        progress: 78,
        template: 'image-classification',
        config: {
            gpuRequired: true,
            gpuCount: 2,
            memoryGB: 16,
            diskGB: 100,
            datasets: ['CIFAR-10', 'ImageNet'],
            environment: 'tensorflow-gpu:2.13'
        },
        resources: {
            gpuUsage: 65,
            memoryUsage: 8.5,
            diskUsage: 42
        },
        training: {
            epochs: 100,
            batchSize: 32,
            learningRate: 0.001,
            currentEpoch: 78,
            loss: 0.245,
            accuracy: 0.892
        },
        owner: 'user_001',
        createdAt: '2024-01-10T08:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z',
        lastActiveAt: '2024-01-15T09:30:00Z',
        cost: 156.80
    },
    {
        id: 'ai_project_002',
        name: '自然语言处理',
        description: '基于Transformer的文本分类和情感分析',
        type: 'nlp',
        framework: 'PyTorch',
        status: 'completed',
        progress: 100,
        template: 'text-analysis',
        config: {
            gpuRequired: true,
            gpuCount: 1,
            memoryGB: 32,
            diskGB: 50,
            datasets: ['IMDB Reviews', 'Twitter Sentiment'],
            environment: 'pytorch-transformers:4.21'
        },
        resources: {
            gpuUsage: 0,
            memoryUsage: 0,
            diskUsage: 28
        },
        training: {
            epochs: 50,
            batchSize: 16,
            learningRate: 0.00005,
            currentEpoch: 50,
            loss: 0.128,
            accuracy: 0.945
        },
        owner: 'user_001',
        createdAt: '2024-01-08T10:00:00Z',
        updatedAt: '2024-01-14T16:00:00Z',
        lastActiveAt: '2024-01-14T15:45:00Z',
        cost: 89.60
    },
    {
        id: 'ai_project_003',
        name: '推荐系统',
        description: '基于协同过滤的个性化推荐算法',
        type: 'custom',
        framework: 'Scikit-learn',
        status: 'pending',
        progress: 23,
        template: 'recommendation-system',
        config: {
            gpuRequired: false,
            memoryGB: 8,
            diskGB: 30,
            datasets: ['MovieLens', 'Amazon Products'],
            environment: 'python-ml:3.9'
        },
        resources: {
            gpuUsage: 0,
            memoryUsage: 0,
            diskUsage: 12
        },
        training: {
            epochs: 200,
            batchSize: 1024,
            learningRate: 0.01,
            currentEpoch: 46
        },
        owner: 'user_002',
        createdAt: '2024-01-12T09:30:00Z',
        updatedAt: '2024-01-13T14:20:00Z',
        cost: 12.30
    }
];

export const mockAIProjectTemplates: AIProjectTemplate[] = [
    {
        id: 'image-classification',
        name: '图像分类',
        description: '基于深度学习的图像分类模板，支持常见的CNN架构',
        type: 'classification',
        framework: 'TensorFlow',
        icon: '🖼️',
        defaultConfig: {
            gpuRequired: true,
            gpuCount: 1,
            memoryGB: 16,
            diskGB: 50,
            environment: 'tensorflow-gpu:2.13'
        },
        tags: ['图像处理', '深度学习', '分类', 'CNN'],
        difficulty: 'beginner',
        estimatedTime: '2-4小时',
        requirements: ['Python 3.8+', 'TensorFlow 2.13+', 'GPU支持']
    },
    {
        id: 'object-detection',
        name: '目标检测',
        description: 'YOLO/R-CNN 目标检测模板，支持实时物体识别',
        type: 'detection',
        framework: 'PyTorch',
        icon: '🎯',
        defaultConfig: {
            gpuRequired: true,
            gpuCount: 2,
            memoryGB: 32,
            diskGB: 100,
            environment: 'pytorch-yolo:8.0'
        },
        tags: ['目标检测', 'YOLO', 'R-CNN', '实时识别'],
        difficulty: 'intermediate',
        estimatedTime: '4-8小时',
        requirements: ['Python 3.8+', 'PyTorch 1.13+', '大容量GPU']
    },
    {
        id: 'text-analysis',
        name: '文本分析',
        description: 'NLP文本分类和情感分析，基于Transformer架构',
        type: 'nlp',
        framework: 'Transformers',
        icon: '📝',
        defaultConfig: {
            gpuRequired: true,
            gpuCount: 1,
            memoryGB: 24,
            diskGB: 60,
            environment: 'transformers:4.21'
        },
        tags: ['NLP', 'BERT', '情感分析', '文本分类'],
        difficulty: 'intermediate',
        estimatedTime: '3-6小时',
        requirements: ['Python 3.8+', 'Transformers 4.21+', 'CUDA支持']
    },
    {
        id: 'time-series',
        name: '时间序列预测',
        description: 'LSTM/GRU时间序列分析，适用于股价、天气等预测',
        type: 'timeseries',
        framework: 'TensorFlow',
        icon: '📈',
        defaultConfig: {
            gpuRequired: false,
            memoryGB: 12,
            diskGB: 40,
            environment: 'tensorflow:2.13'
        },
        tags: ['时间序列', 'LSTM', 'GRU', '预测分析'],
        difficulty: 'beginner',
        estimatedTime: '2-4小时',
        requirements: ['Python 3.8+', 'TensorFlow 2.13+', 'Pandas']
    }
];